import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { cn } from "../../lib/cn";

export interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  onClear?: () => void;
  placeholder?: string;
  maxWidth?: number | string;
  className?: string;
  showSubmitButton?: boolean;
}

/** claudesign search field. Mirrors `.search` / `.search .go` /
 *  `.search:focus-within` from docs/redesign/ommuse-theme.css. */
export const SearchField: React.FC<SearchFieldProps> = ({
  value,
  onChange,
  onSubmit,
  onClear,
  placeholder = "Search...",
  maxWidth,
  className,
  showSubmitButton,
}) => {
  const handleClear = () => {
    onChange("");
    onClear?.();
  };

  const hasSubmit = Boolean(onSubmit || showSubmitButton);

  return (
    <div
      style={
        maxWidth
          ? ({
              "--search-max-w":
                typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
            } as React.CSSProperties)
          : undefined
      }
      className={cn(
        "flex items-center gap-2 w-full md:w-auto md:max-w-[var(--search-max-w,380px)] bg-surface-2 border border-border rounded-[30px]",
        "pl-3 sm:pl-[14px] pr-1.5 sm:pr-[6px] py-1 md:py-[5px] min-h-[36px] md:min-h-[40px] shadow-edge-hi",
        "transition-[border-color,box-shadow] duration-150",
        "focus-within:border-[color-mix(in_oklab,var(--cyan)_55%,var(--border))]",
        "focus-within:shadow-[0_0_0_3px_var(--accent-soft),var(--edge-hi)]",
        "focus-within:outline focus-within:outline-2 focus-within:outline-[var(--cyan)] focus-within:outline-offset-2",
        className,
      )}
    >
      <svg
        viewBox="0 0 24 24"
        aria-hidden
        className="w-[15px] h-[15px] sm:w-[16px] sm:h-[16px] shrink-0 text-cyan fill-current"
      >
        <path d="M12 2l1.6 4.4L18 8l-4.4 1.6L12 14l-1.6-4.4L6 8l4.4-1.6L12 2z" />
      </svg>
      <input
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit?.();
        }}
        className={cn(
          "flex-1 min-w-0 bg-transparent border-none outline-none",
          "text-body font-sans text-[13px] sm:text-[13.5px] placeholder:text-muted",
        )}
      />
      {value && (
        <button
          type="button"
          onClick={handleClear}
          className="p-1 rounded-full text-muted hover:text-white transition-colors flex items-center justify-center shrink-0 cursor-pointer"
          aria-label="Clear search"
        >
          <CloseRoundedIcon style={{ fontSize: 15 }} />
        </button>
      )}
      {hasSubmit ? (
        <button
          type="button"
          onClick={() => onSubmit?.()}
          className={cn(
            "bg-accent-grad text-cyan-ink border-0 font-sans font-semibold text-[12px] sm:text-[13px]",
            "px-2.5 sm:px-[14px] py-1 sm:py-[6px] rounded-[20px] cursor-pointer shrink-0",
            "shadow-[var(--glow),inset_0_1px_0_rgba(255,255,255,.28)]",
            "transition-[filter,box-shadow] duration-150 hover:brightness-[1.07] hover:shadow-glow-strong",
          )}
        >
          Search
        </button>
      ) : (
        <button
          type="button"
          tabIndex={-1}
          className={cn(
            "hidden sm:inline-flex bg-accent-grad text-cyan-ink border-0 font-sans font-semibold text-[13px]",
            "px-[14px] py-[6px] rounded-[20px] cursor-pointer shrink-0",
            "shadow-[var(--glow),inset_0_1px_0_rgba(255,255,255,.28)]",
            "transition-[filter,box-shadow] duration-150 hover:brightness-[1.07] hover:shadow-glow-strong",
          )}
        >
          Search
        </button>
      )}
    </div>
  );
};

export default SearchField;
